<script setup lang="ts">
import { computed, h, onMounted, ref } from 'vue';
import { NSpace, NTag, type DataTableColumns, type TagProps } from 'naive-ui';
import { IconArrowBack } from '@/components/design/icons';
import {
  ReviewProjectReportType,
  TaskStatus,
  type ReviewProjectAutoTagTaskInDb,
  type ReviewProjectReportTaskInDb,
} from '@/api';
import {
  ReviewProjectService,
  type ReviewProjectTaskGroupDetail,
  type ReviewProjectTaskGroupSummary,
} from '@/services/reviewProjectService';
import { useReviewProjectStore } from '@/stores/reviewProjectStore';
import { formatDateTimeForTable } from '@/utils/datetimeUtils';
import { tagChipColor } from '@/utils/tagColors';
import { useGaiaToast } from '@/composables/useGaiaToast';

const props = defineProps<{
  projectUid: string;
}>();

const reviewProjectStore = useReviewProjectStore();
const message = useGaiaToast();

const loading = ref<boolean>(false);
const detailLoading = ref<boolean>(false);

onMounted(async () => {
  // Refetch on every visit (group statuses move while runs execute), but keep
  // showing the cached list from a previous mount while doing so; the spinner
  // and the error toast are for the first load only — a failed background
  // refresh keeps the stale list instead of nagging.
  const isFirstLoad = reviewProjectStore.taskGroups === null;
  if (isFirstLoad) loading.value = true;
  try {
    reviewProjectStore.taskGroups = await ReviewProjectService.listTaskGroups(props.projectUid);
  } catch (error) {
    if (isFirstLoad) {
      message.error(error instanceof Error ? error.message : 'Failed to fetch task groups.');
    }
  } finally {
    loading.value = false;
  }
});

async function openGroup(uid: string): Promise<void> {
  reviewProjectStore.selectedTaskGroupUid = uid;
  // Same refetch-on-visit pattern as the list: cached detail shows instantly,
  // a fresh fetch updates the task statuses behind it.
  const isFirstLoad = !(uid in reviewProjectStore.taskGroupDetails);
  if (isFirstLoad) detailLoading.value = true;
  try {
    reviewProjectStore.taskGroupDetails[uid] = await ReviewProjectService.getTaskGroup(
      props.projectUid,
      uid
    );
  } catch (error) {
    if (isFirstLoad) {
      message.error(error instanceof Error ? error.message : 'Failed to fetch task group.');
      reviewProjectStore.selectedTaskGroupUid = null;
    }
  } finally {
    detailLoading.value = false;
  }
}

const GROUP_STATUS_TYPE: Record<TaskStatus, TagProps['type']> = {
  [TaskStatus.PENDING]: 'default',
  [TaskStatus.RUNNING]: 'info',
  [TaskStatus.DONE]: 'success',
  [TaskStatus.FAILED]: 'error',
  [TaskStatus.PARTIAL]: 'warning',
  [TaskStatus.SKIPPED]: 'warning',
  [TaskStatus.CANCELLED]: 'default',
  [TaskStatus.WARNING]: 'warning',
};

const TASK_STATUS_TYPE: Record<TaskStatus, TagProps['type']> = {
  [TaskStatus.PENDING]: 'default',
  [TaskStatus.RUNNING]: 'info',
  [TaskStatus.DONE]: 'success',
  [TaskStatus.FAILED]: 'error',
  [TaskStatus.PARTIAL]: 'warning',
  [TaskStatus.SKIPPED]: 'warning',
  [TaskStatus.CANCELLED]: 'default',
  [TaskStatus.WARNING]: 'warning',
};

function rowKey(row: { uid: string }): string {
  return row.uid;
}

// Auto-tag groups can carry many options; naming them all makes the list
// rows collide, so the label caps out and the rest becomes a count.
const GROUP_LABEL_MAX_OPTIONS = 3;

function groupLabel(group: ReviewProjectTaskGroupSummary | ReviewProjectTaskGroupDetail): string {
  if (group.task_type === 'report') {
    return group.report_type === ReviewProjectReportType.DUE_DILIGENCE
      ? 'Due diligence report'
      : 'Report';
  }
  const names = group.options.map(option => option.name);
  if (names.length === 0) return 'Auto-tag';
  if (names.length <= GROUP_LABEL_MAX_OPTIONS) return names.join(', ');
  return `${names.slice(0, GROUP_LABEL_MAX_OPTIONS).join(', ')} +${
    names.length - GROUP_LABEL_MAX_OPTIONS
  } more`;
}

function taskCountSummary(group: ReviewProjectTaskGroupSummary): string {
  const counts = group.task_status_counts;
  const total = Object.values(counts).reduce((sum, count) => sum + count, 0);
  const done = counts[TaskStatus.DONE] ?? 0;
  return `${done}/${total} done`;
}

const autoTagColumns: DataTableColumns<ReviewProjectAutoTagTaskInDb> = [
  { title: 'File', key: 'review_project_file_uid', ellipsis: { tooltip: true } },
  {
    title: 'Status',
    key: 'status',
    width: 110,
    render: row =>
      h(
        NTag,
        { size: 'small', bordered: false, type: TASK_STATUS_TYPE[row.status] },
        () => row.status
      ),
  },
  {
    title: 'Matched tags',
    key: 'matched_tags',
    render: row =>
      row.matched_tags.length
        ? h(NSpace, { size: 4 }, () =>
            row.matched_tags.map(tag =>
              h(
                NTag,
                { size: 'small', round: true, bordered: false, color: tagChipColor(tag.color) },
                () => tag.name
              )
            )
          )
        : '—',
  },
  {
    title: 'Reasoning',
    key: 'reasoning',
    ellipsis: { tooltip: true },
    render: row => row.reasoning ?? '—',
  },
  {
    title: 'Error',
    key: 'error',
    ellipsis: { tooltip: true },
    render: row => (row.error ? h('span', { style: 'color: var(--error-color)' }, row.error) : '—'),
  },
];

// Report groups have a single task with no file link or tag results.
const reportColumns: DataTableColumns<ReviewProjectReportTaskInDb> = [
  {
    title: 'Status',
    key: 'status',
    width: 110,
    render: row =>
      h(
        NTag,
        { size: 'small', bordered: false, type: TASK_STATUS_TYPE[row.status] },
        () => row.status
      ),
  },
  {
    title: 'Error',
    key: 'error',
    ellipsis: { tooltip: true },
    render: row => (row.error ? h('span', { style: 'color: var(--error-color)' }, row.error) : '—'),
  },
];

const selectedGroupDetail = computed<ReviewProjectTaskGroupDetail | undefined>(() =>
  reviewProjectStore.selectedTaskGroupUid
    ? reviewProjectStore.taskGroupDetails[reviewProjectStore.selectedTaskGroupUid]
    : undefined
);
</script>

<template>
  <n-spin v-if="loading" style="margin-top: 48px" />

  <!-- Group list -->
  <template v-else-if="!reviewProjectStore.selectedTaskGroupUid">
    <n-empty
      v-if="!reviewProjectStore.taskGroups?.length"
      description="No tasks yet"
      style="margin-top: 48px"
    />
    <n-list v-else hoverable clickable data-tour="rp.tasks">
      <n-list-item
        v-for="group in reviewProjectStore.taskGroups"
        :key="group.uid"
        @click="openGroup(group.uid)"
      >
        <n-thing>
          <template #header>
            <n-ellipsis style="max-width: 100%">{{ groupLabel(group) }}</n-ellipsis>
          </template>
          <template #description>
            {{ formatDateTimeForTable(group.created_at) }} · {{ taskCountSummary(group) }}
            <n-ellipsis v-if="group.task_type === 'report'" style="max-width: 100%; display: block">
              {{ group.instruction }}
            </n-ellipsis>
          </template>
        </n-thing>
        <template #suffix>
          <n-tag size="small" :type="GROUP_STATUS_TYPE[group.status]">{{ group.status }}</n-tag>
        </template>
      </n-list-item>
    </n-list>
  </template>

  <!-- Group detail (task list) -->
  <template v-else>
    <n-space vertical :size="12" style="height: 100%">
      <n-space align="center" :size="8">
        <n-button quaternary size="small" @click="reviewProjectStore.selectedTaskGroupUid = null">
          <template #icon>
            <n-icon><IconArrowBack /></n-icon>
          </template>
          Back
        </n-button>
        <template v-if="selectedGroupDetail">
          <span style="font-weight: 600">{{ groupLabel(selectedGroupDetail) }}</span>
          <n-tag size="small" :type="GROUP_STATUS_TYPE[selectedGroupDetail.status]">
            {{ selectedGroupDetail.status }}
          </n-tag>
        </template>
      </n-space>

      <n-spin v-if="detailLoading" style="margin-top: 48px" />
      <n-data-table
        v-else-if="selectedGroupDetail?.task_type === 'auto_tag'"
        :columns="autoTagColumns"
        :data="selectedGroupDetail.tasks"
        :row-key="rowKey"
        size="small"
      />
      <template v-else-if="selectedGroupDetail?.task_type === 'report'">
        <n-text depth="3">
          <n-ellipsis expand-trigger="click" :line-clamp="2" :tooltip="false">
            {{ selectedGroupDetail.instruction }}
          </n-ellipsis>
        </n-text>
        <n-data-table
          :columns="reportColumns"
          :data="selectedGroupDetail.tasks"
          :row-key="rowKey"
          size="small"
        />
      </template>
    </n-space>
  </template>
</template>

<style scoped>
/* n-list/n-thing nest several flex children (min-width: auto by default), so
   long labels/instructions size every level to the content and push the row
   past the pane's right edge instead of letting the n-ellipsis truncate.
   With a #header slot, n-thing puts header AND description inside
   .n-thing-header-wrapper (flex: 1), so that level needs unlocking too. */
.n-list :deep(.n-list-item__main),
.n-list :deep(.n-thing-main),
.n-list :deep(.n-thing-header-wrapper),
.n-list :deep(.n-thing-header__title) {
  min-width: 0;
}
</style>
